import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Body, CodeBlock, MenuPath, Note, SectionDivider, Tip } from '../ui';

// ── Page ────────────────────────────────────────────────────────────────────

const ActionsMenusDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- Overview -- */}
      <h2
        id="radial-menus"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.menus.radialMenusTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.menus.radialMenusBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.menus.radialMenusBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.menus.radialMenusBody3"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.actionsMenus.customReturnBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <SectionDivider />

      {/* -- Minimal example -- */}
      <h2
        id="minimal-example"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.menus.minimalExampleTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.menus.minimalExampleBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <CodeBlock>
        {`import os
import maya.cmds as mc
import sbgui.widgets.radial_menu as rm
import touchpose.constants as _tp_constants

try:
    from PySide6 import QtGui
except ImportError:
    from PySide2 import QtGui

from functools import partial

_center_icon = os.path.join(_tp_constants.ICONPATH, "touchpose_icon.svg")

# Strip namespace for display label
namespace = ":".join(touch_control.split("|")[-1].split(":")[:-1]) + ":"
display_name = touch_control.replace(namespace, "")

item_list = [
    {"position": "N", "text": display_name, "func": partial(mc.select, touch_control)},
]

touch_menu = rm.RadialMenu(center_icon=_center_icon)
for item in item_list:
    menu_item = rm.RadialMenuItem(position=item["position"], text=item["text"])
    menu_item.connect(item["func"])
    touch_menu.add_item(menu_item)

touch_menu.popup(QtGui.QCursor.pos())
custom_return = touch_menu  # Required — keeps the menu alive`}
      </CodeBlock>

      <SectionDivider />

      {/* -- Position Conventions -- */}
      <h2
        id="position-conventions"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.menus.positionConventionsTitle')}
      </h2>
      <Body>{t('docs.menus.positionConventionsBody')}</Body>
      <table className="w-full border-collapse text-[13px] mb-[20px]">
        <thead>
          <tr className="border-b-2 border-surface-hover">
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.menus.positionTablePosition')}
            </th>
            <th className="text-left px-[12px] py-[8px] font-display font-bold text-content-primary">
              {t('docs.menus.positionTableReservedFor')}
            </th>
          </tr>
        </thead>
        <tbody>
          {[
            ['N', t('docs.menus.positionN')],
            ['E', t('docs.menus.positionE')]
          ].map(([pos, use], i) => (
            <tr
              key={i}
              className={`border-b border-surface-secondary ${i % 2 === 0 ? 'bg-surface-base' : 'bg-surface-secondary'}`}
            >
              <td className="px-[12px] py-[8px]">
                <MenuPath>{pos}</MenuPath>
              </td>
              <td className="px-[12px] py-[8px] text-content-secondary">{use}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionDivider />

      {/* -- Space Switching -- */}
      <h2
        id="space-switching"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.menus.spaceSwitchingTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.menus.spaceSwitchingBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <CodeBlock>
        {`if mc.objExists(f"{touch_control}.space"):
    def space_switch(node, value):
        # Preserve world-space position during the switch
        matrix = mc.xform(node, q=True, ws=True, matrix=True)
        mc.setAttr(f"{node}.space", value)
        mc.xform(node, ws=True, matrix=matrix)

    space_list = mc.attributeQuery("space", node=touch_control, listEnum=1)[0].split(":")
    current_value = mc.getAttr(f"{touch_control}.space")

    for i, space in enumerate(space_list):
        text = f"Space  [  {space}  ]"
        if i == current_value:
            text = f"Space  [ * {space} * ]"   # Mark the active space
        item_list.append({
            "position": "",                    # Empty string -> column area
            "text": text,
            "func": partial(space_switch, touch_control, i)
        })`}
      </CodeBlock>

      <SectionDivider />

      {/* -- IK/FK Switching -- */}
      <h2
        id="ikfk-switching"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.menus.ikfkSwitchingTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.menus.ikfkSwitchingBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <CodeBlock>
        {`ik_fk_info = ""
if mc.objExists(f"{touch_control}.match_nodes"):
    param_node = mc.listConnections(f"{touch_control}.match_nodes")
    if param_node:
        param_node = param_node[0]
        switch_attr = f"{param_node}.ikfk_switch"
        if mc.objExists(switch_attr):
            def switch(param_node, value):
                mc.setAttr(f"{param_node}.ikfk_switch", value)
                sb_run_switch(param_node, "ikfk_switch")

            state = mc.getAttr(switch_attr)          # IK = 0, FK = 1
            switch_func = partial(switch, param_node, not state)
            ik_fk_info = "Switch to [ FK ]"
            if state:
                ik_fk_info = "Switch to [ IK ]"

# Add to item_list at position "E" (reserved for IK/FK)
if ik_fk_info:
    item_list.append({"position": "E", "text": ik_fk_info, "func": switch_func})`}
      </CodeBlock>

      <Note label={t('docs.menus.noteLabel')}>
        <Trans
          i18nKey="docs.menus.ikfkNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Full Example -- */}
      <h2
        id="full-example"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.menus.fullExampleTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.menus.fullExampleBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <CodeBlock>
        {`import os
import maya.cmds as mc
import sbgui.widgets.radial_menu as rm
import touchpose.constants as _tp_constants

try:
    from PySide6 import QtGui
except ImportError:
    from PySide2 import QtGui

from functools import partial

_center_icon = os.path.join(_tp_constants.ICONPATH, "touchpose_icon.svg")
item_list = []

namespace = ":".join(touch_control.split("|")[-1].split(":")[:-1]) + ":"

# ── IK/FK ────────────────────────────────────────────────────────────────────
ik_fk_info = ""
if mc.objExists(f"{touch_control}.match_nodes"):
    param_node = mc.listConnections(f"{touch_control}.match_nodes")
    if param_node:
        param_node = param_node[0]
        switch_attr = f"{param_node}.ikfk_switch"
        if mc.objExists(switch_attr):
            def switch(param_node, value):
                mc.setAttr(f"{param_node}.ikfk_switch", value)
                sb_run_switch(param_node, "ikfk_switch")
            state = mc.getAttr(switch_attr)
            switch_func = partial(switch, param_node, not state)
            ik_fk_info = "Switch to [ FK ]"
            if state:
                ik_fk_info = "Switch to [ IK ]"

# ── Spaces ───────────────────────────────────────────────────────────────────
if mc.objExists(f"{touch_control}.space"):
    def space_switch(node, value):
        matrix = mc.xform(node, q=True, ws=True, matrix=True)
        mc.setAttr(f"{node}.space", value)
        mc.xform(node, ws=True, matrix=matrix)

    space_list = mc.attributeQuery("space", node=touch_control, listEnum=1)[0].split(":")
    current_value = mc.getAttr(f"{touch_control}.space")

    for i, space in enumerate(space_list):
        text = f"Space  [  {space}  ]"
        if i == current_value:
            text = f"Space  [ * {space} * ]"
        item_list.append({
            "position": "",                    # Empty string -> column area
            "text": text,
            "func": partial(space_switch, touch_control, i)
        })

# ── Control name (N) ─────────────────────────────────────────────────────────
item_list.append({
    "position": "N",
    "text": touch_control.replace(namespace, ""),
    "func": partial(mc.select, touch_control),
})

# ── IK/FK (E) ────────────────────────────────────────────────────────────────
if ik_fk_info:
    item_list.append({"position": "E", "text": ik_fk_info, "func": switch_func})

# ── Build and show ───────────────────────────────────────────────────────────
touch_menu = rm.RadialMenu(center_icon=_center_icon)
for item in item_list:
    menu_item = rm.RadialMenuItem(position=item["position"], text=item["text"])
    menu_item.connect(item["func"])
    touch_menu.add_item(menu_item)

touch_menu.popup(QtGui.QCursor.pos())
custom_return = touch_menu`}
      </CodeBlock>

      <Tip label={t('docs.menus.tipLabel')}>
        <Trans
          i18nKey="docs.menus.fullExampleTip"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Tip>
    </article>
  );
};

export default ActionsMenusDoc;
